import { ReactElement } from 'react';
import { AbsoluteFill } from 'remotion';

// Hooks
import {
  useDiagonalWipeTransition,
  DiagonalWipeTransitionConfig,
} from '@/hooks/useDiagonalWipeTransition';
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';
import { combineClasses } from '@/utils';

export interface DiagonalWipeTransitionProps
  extends Omit<DiagonalWipeTransitionConfig, 'startFrame'> {
  /** Frame when the transition should start */
  startFrame: number;
  /** Z-index for the overlay */
  zIndex?: number;
}

/**
 * DiagonalWipeTransition Component
 *
 * Renders the diagonal stripe overlay that creates a wipe transition effect.
 * This component expands diagonal stripes BIDIRECTIONALLY (both inward and outward)
 * to cover the screen, then collapses them to reveal the next scene.
 *
 * Usage: Place this component on top of both outgoing and incoming scenes
 * to create the transition effect.
 */
export const DiagonalWipeTransition = ({
  startFrame,
  expandDuration,
  collapseDuration,
  holdDuration,
  skewAngle,
  zIndex = 50,
}: DiagonalWipeTransitionProps): ReactElement | null => {
  const {
    topLeftWidth,
    bottomRightWidth,
    topLeftExpandTranslateX,
    bottomRightExpandTranslateX,
    topLeftCollapseTranslateX,
    bottomRightCollapseTranslateX,
    collapseProgress,
    isTransitioning,
    skewAngle: computedSkewAngle,
  } = useDiagonalWipeTransition({
    startFrame,
    expandDuration,
    collapseDuration,
    holdDuration,
    skewAngle,
  });
  const { colors } = employeeYearInReviewTheme;

  // Don't render before transition starts
  if (!isTransitioning && collapseProgress === 0) {
    return null;
  }

  // Combine expand and collapse translations
  // During expand phase: expandTranslate is active, collapseTranslate is 0
  // During collapse phase: expandTranslate stays at final value, collapseTranslate animates
  const topLeftTotalTranslateX =
    topLeftExpandTranslateX + topLeftCollapseTranslateX;
  const bottomRightTotalTranslateX =
    bottomRightExpandTranslateX + bottomRightCollapseTranslateX;

  return (
    <AbsoluteFill
      className="pointer-events-none overflow-hidden"
      style={{ zIndex }}
    >
      {/* Top-left diagonal stripe */}
      <div
        className={combineClasses(
          'absolute left-[580px] top-0 h-full origin-top-left -ml-[15%]',
          colors.bgPrimary,
        )}
        style={{
          width: `${topLeftWidth}%`,
          transform: `translateX(${topLeftTotalTranslateX}px) skewX(${computedSkewAngle}deg)`,
        }}
      />

      {/* Bottom-right diagonal stripe */}
      <div
        className={combineClasses(
          'absolute bottom-0 right-[580px] h-full origin-bottom-right -mr-[15%]',
          colors.bgPrimary,
        )}
        style={{
          width: `${bottomRightWidth}%`,
          transform: `translateX(${bottomRightTotalTranslateX}px) skewX(${computedSkewAngle}deg)`,
        }}
      />
    </AbsoluteFill>
  );
};
